import { Box, Link } from "@mui/material";
import { MouseEvent, ReactNode } from "react";

import type { ScoutToolPart } from "./message/scoutChatMessage";

import { GRAPHQL_LABELS, INTROSPECTION_SUBJECT } from "./graphqlLabels";

export type ToolRenderContext = {
  tool: ScoutToolPart;
  /** True when the tool has been dispatched but output isn't available yet. */
  isRunning: boolean;
};

export type ToolRenderer = (ctx: ToolRenderContext) => ReactNode;

/**
 * Domain-specific acronyms and proper nouns that should keep their canonical
 * casing when a CamelCase or snake_case identifier is humanized into prose.
 *
 * The map is keyed by the lowercased token (post-split) and the value is the
 * preferred display form. Order matters within combined tokens — we try
 * multi-token sequences first (e.g. "tier ii") before single tokens.
 */
const DOMAIN_ACRONYMS: Record<string, string> = {
  ca: "CA",
  ny: "NY",
  us: "US",
  uk: "UK",
  eu: "EU",
  epa: "EPA",
  rcra: "RCRA",
  epcra: "EPCRA",
  cercla: "CERCLA",
  caa: "CAA",
  cwa: "CWA",
  cfr: "CFR",
  ccr: "CCR",
  nycrr: "NYCRR",
  serc: "SERC",
  lepc: "LEPC",
  lqg: "LQG",
  sqg: "SQG",
  vsqg: "VSQG",
  sds: "SDS",
  cas: "CAS",
  ehs: "EHS",
  tpq: "TPQ",
  npdes: "NPDES",
  ash: "Ash",
  scout: "Scout",
  encamp: "Encamp",
};

/**
 * Multi-token acronym sequences. Applied AFTER token split but BEFORE
 * per-token replacement, so e.g. ["tier", "ii"] → ["Tier II"] keeps the
 * roman numeral together.
 */
const DOMAIN_PHRASES: Array<{ from: string[]; to: string }> = [
  { from: ["tier", "ii"], to: "Tier II" },
  { from: ["tier", "i", "i"], to: "Tier II" },
  { from: ["tier", "iii"], to: "Tier III" },
];

function applyDomainPhrases(tokens: string[]): string[] {
  const lowered = tokens.map((t) => t.toLowerCase());
  const out: string[] = [];
  let i = 0;
  outer: while (i < tokens.length) {
    for (const { from, to } of DOMAIN_PHRASES) {
      if (lowered.slice(i, i + from.length).join(" ") === from.join(" ")) {
        out.push(to);
        i += from.length;
        continue outer;
      }
    }
    out.push(tokens[i]);
    i += 1;
  }
  return out;
}

function applyDomainAcronyms(token: string): string {
  return DOMAIN_ACRONYMS[token.toLowerCase()] ?? token;
}

/**
 * Split a snake/kebab/CamelCase identifier into tokens. Doesn't apply any
 * casing yet — that's the caller's job (humanize for prose, raw for lookup).
 */
function tokenize(name: string): string[] {
  return name
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

function humanize(toolName: string): string {
  // Split snake_case / kebab-case AND CamelCase, then preserve domain
  // acronyms (Tier II, CA, RCRA, EPCRA, LQG/SQG/VSQG, etc.) so we don't
  // produce things like "tier ii" or "rcra" in customer-facing copy.
  const tokens = tokenize(toolName);
  if (tokens.length === 0) return toolName;
  const phrased = applyDomainPhrases(tokens);
  const cased = phrased.map((token, idx) => {
    // If the phrase pass already produced a properly-cased multi-word token
    // (contains a space), trust it.
    if (token.includes(" ")) return token;
    const acronym = applyDomainAcronyms(token);
    if (acronym !== token) return acronym;
    if (idx === 0) return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
    return token.toLowerCase();
  });
  return cased.join(" ");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function renderUnderline(children: ReactNode): ReactNode {
  return (
    <Box component="span" sx={{ textDecoration: "underline" }}>
      {children}
    </Box>
  );
}

function readString(value: unknown, key: string): string | null {
  if (isRecord(value) && key in value) {
    const v = value[key];
    if (typeof v === "string" && v.trim().length > 0) return v.trim();
  }
  return null;
}

function readObject(value: unknown, key: string): Record<string, unknown> | null {
  if (!isRecord(value) || !(key in value)) {
    return null;
  }

  const candidate = value[key];
  return isRecord(candidate) ? candidate : null;
}

/** First non-empty string value among the given keys. */
function readFirstString(value: unknown, keys: string[]): string | null {
  for (const key of keys) {
    const v = readString(value, key);
    if (v !== null) return v;
  }
  return null;
}

function truncate(value: string, max = 80): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1).trimEnd()}…`;
}

export const TOOL_PARAMETER_MAX_CHARS = 60;

function parseFilterQuery(search: string | null): Map<string, string> {
  const filters = new Map<string, string>();
  if (!search) return filters;
  const matches = search.matchAll(/([A-Za-z][A-Za-z0-9_]*):("[^"]+"|[^ ]+)/g);
  for (const match of matches) {
    const [, key, rawValue] = match;
    if (!key || !rawValue) continue;
    filters.set(key, rawValue.replace(/"/g, ""));
  }
  return filters;
}

function normalizeStateList(raw: string): string {
  return raw
    .split(/[|,]/)
    .map((state) => state.trim())
    .filter(Boolean)
    .join(", ");
}

function sanitizeApiLookupQuery(query: string | null): string | null {
  if (!query) return null;
  const trimmed = query.trim();
  if (!trimmed) return null;

  const filters = parseFilterQuery(trimmed);
  if (filters.size > 0) {
    const state = filters.get("state");
    if (state) {
      const states = normalizeStateList(state);
      return states ? `facilities in ${states}` : "facilities";
    }
    if (filters.has("facilityId")) return "a facility";
    if (filters.has("ids")) return "selected facilities";
    if (filters.has("tenantId")) return "facilities";
  }

  const lowered = trimmed.toLowerCase();
  const includesTechnicalTokens =
    /\b(?:omnisearch|tenantid|facilityid|personid|page|pagesize|graphql|lookup)\b/i.test(trimmed);
  if (includesTechnicalTokens) {
    if (lowered.includes("facilit")) return "facilities";
    if (lowered.includes("report")) return "reports";
    if (lowered.includes("task")) return "tasks";
    return null;
  }

  return trimmed;
}

function sanitizeComplianceSearchQuery(query: string | null): string | null {
  if (!query) return null;
  const trimmed = query.trim();
  if (!trimmed) return null;

  if (
    /\b(?:compliance:[a-z0-9-]+:[A-Za-z0-9._-]+|validation-rule|tenantid|facilityid|personid|omnisearch)\b/i.test(
      trimmed
    )
  ) {
    return null;
  }

  return trimmed;
}

function isInternalComplianceIdentifier(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (/^compliance:[a-z0-9-]+:[A-Za-z0-9._-]+$/i.test(trimmed)) return true;
  if (trimmed.includes(":validation-rule:")) return true;
  if (trimmed.split(":").length >= 3) return true;
  if (/\b[a-z]+\.[a-z]+\.[a-z0-9._-]+\.[0-9a-f]{6,}\b/i.test(trimmed)) return true;
  return false;
}

function sanitizeComplianceIdentifier(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (isInternalComplianceIdentifier(trimmed)) return null;
  return trimmed;
}

function firstFriendlyComplianceIdentifier(candidates: Array<string | null>): string | null {
  for (const candidate of candidates) {
    const friendly = sanitizeComplianceIdentifier(candidate);
    if (friendly) return friendly;
  }
  return null;
}

/**
 * Short display label for a URL — hostname stripped of `www.`, falling back
 * to a lightweight regex trim if the URL can't be parsed. Paths are dropped;
 * the full URL stays on the clickable anchor via `href`.
 */
function displayUrl(url: string): string {
  try {
    const host = new URL(url).hostname.replace(/^www\./i, "");
    if (host.length > 0) return host;
  } catch {
    // fall through
  }
  return url.replace(/^https?:\/\/(www\.)?/i, "").split("/")[0];
}

function stopPropagation(event: MouseEvent<HTMLAnchorElement>) {
  // The enclosing ToolCallGroup row is a click target that toggles expansion;
  // without this, clicking an inline source URL would also collapse/expand.
  event.stopPropagation();
}

/**
 * Extracts the URLs returned by a native provider web_search tool.
 * Handles both shapes:
 *  - Anthropic `webSearch_20250305` → `{ type: "web_search_result", url }[]`
 *  - OpenAI `webSearch` → `{ action?: { url? }, sources?: { type, url }[] }`
 */
function readWebSearchUrls(output: unknown): string[] {
  const urls: string[] = [];
  const push = (value: unknown) => {
    if (typeof value === "string" && value.trim().length > 0) {
      urls.push(value.trim());
    }
  };

  if (Array.isArray(output)) {
    for (const item of output) push(readString(item, "url"));
  } else if (isRecord(output)) {
    const record = output;
    if (record.action && typeof record.action === "object") {
      push(readString(record.action, "url"));
    }
    if (Array.isArray(record.sources)) {
      for (const src of record.sources) push(readString(src, "url"));
    }
  }

  return Array.from(new Set(urls));
}

/**
 * Renders a "Verb <thing> for 'query'" pattern with a running/done verb pair.
 * If no query param is found on the input, falls back to just the phrase.
 */
function searchPhrase(options: {
  runningVerb: string;
  doneVerb: string;
  object: string;
  query: string | null;
  isRunning: boolean;
}): ReactNode {
  const { runningVerb, doneVerb, object, query, isRunning } = options;
  const verb = isRunning ? runningVerb : doneVerb;
  if (!query) {
    return isRunning ? `${verb} ${object}…` : `${verb} ${object}`;
  }
  const displayQuery = truncate(query, TOOL_PARAMETER_MAX_CHARS);
  const trailing = isRunning && !displayQuery.endsWith("…") ? "…" : "";
  return (
    <>
      {verb} {object} for {renderUnderline(displayQuery)}
      {trailing}
    </>
  );
}

/**
 * Fallback for any tool that doesn't have a custom renderer.
 * Uses the backend-provided `toolDisplayName` if present, else humanizes `toolName`.
 */
const genericRenderer: ToolRenderer = ({ tool, isRunning }) => {
  const fallbackLabel =
    /^(?:get|search|describe|execute|query|list|update|create|delete|fetch|load|analyze|summarize|switch|write|read|clear|browse)_[a-z0-9_]+$/i.test(
      tool.toolName
    ) || /\b(?:graphql|omnisearch|validator|pipeline)\b/i.test(tool.toolName)
      ? "Checking information"
      : humanize(tool.toolName);
  const label = tool.toolDisplayName ?? fallbackLabel;
  return isRunning ? `${label}…` : label;
};

const QUERY_INPUT_KEYS = ["query", "search", "term", "q", "topic"];

function readGraphQLVariables(value: unknown): Record<string, unknown> | null {
  return readObject(value, "variables");
}

/**
 * Read the first top-level FIELD name selected in a GraphQL query — i.e. the
 * actual entity being queried, not the model-invented operation name.
 *
 * For `query PickCaFacility { facility(id: $id) { ... } }` this returns
 * `"facility"` rather than `"PickCaFacility"`. Field names are conventionally
 * stable lowerCamelCase from the schema, so they map cleanly to the curated
 * GRAPHQL_LABELS table. Operation names are whatever the model felt like
 * writing, so we don't trust them.
 *
 * GraphQL aliases are stripped: `c1: reportingFacilityChemicals(...)` returns
 * `"reportingFacilityChemicals"` not `"c1"`.
 */
function readGraphQLTopLevelField(value: unknown): string | null {
  const query = readString(value, "query");
  if (!query) return null;

  // Strip top-level operation header like `query Foo($x: Int) ` /
  // `mutation Bar { ` so the first field token is what we land on.
  const stripped = query.replace(
    /^\s*(?:query|mutation|subscription)\s+\w*\s*(?:\([^)]*\))?\s*/u,
    ""
  );

  // Match the first selection inside the leading `{ ... }`. Two shapes:
  //   { fieldName ...      → group 2 is the field
  //   { alias: fieldName … → group 2 is alias, group 3 is the field
  // Aliases are common when the model batches multiple calls into one query.
  const match = stripped.match(
    /^\s*\{\s*([A-Za-z_][A-Za-z0-9_]*)(?:\s*:\s*([A-Za-z_][A-Za-z0-9_]*))?/u
  );
  if (!match) return null;
  // If group 2 (real field after alias) matched, prefer it over the alias.
  return match[2] ?? match[1] ?? null;
}

const INTROSPECTION_FIELDS = new Set(["__type", "__schema", "__typename"]);

function isIntrospectionQuery(toolInput: unknown): boolean {
  const field = readGraphQLTopLevelField(toolInput);
  if (field && INTROSPECTION_FIELDS.has(field)) return true;

  // Fallback: catch queries where the model nests __type/__schema deeper.
  const query = readString(toolInput, "query");
  if (!query) return false;
  return /\b__(?:type|schema)\s*\(/.test(query);
}

function describeGraphQLOperation(toolInput: unknown): string {
  if (isIntrospectionQuery(toolInput)) {
    return INTROSPECTION_SUBJECT;
  }

  // The curated map is keyed by the actual GraphQL field, not the
  // model-invented operation name. Fields are stable lowerCamelCase from the
  // schema; operation names are whatever the model wrote.
  const field = readGraphQLTopLevelField(toolInput);
  if (field) {
    const label = GRAPHQL_LABELS[field];
    if (label) {
      const variables = readGraphQLVariables(toolInput);
      const search = readString(variables, "search");
      const refined = label.refine?.(variables, search);
      return refined ?? label.subject;
    }
  }

  // Unknown field → don't render the model's invented operation name (we
  // used to fall through to humanize() but it produced ugly things like
  // "pick ca facility"). Stay generic.
  return "Encamp data";
}

function summarizeGraphQLExecution(toolInput: unknown): string {
  // describeGraphQLOperation handles introspection, field-based lookup in
  // the curated map (with optional refine() based on search/variables), and
  // the generic fallback. Keep this thin so the renderer logic stays in one
  // place.
  return describeGraphQLOperation(toolInput);
}

function graphQLLookupPhrase(options: {
  runningVerb: string;
  doneVerb: string;
  subject: string;
  isRunning: boolean;
}): string {
  const verb = options.isRunning ? options.runningVerb : options.doneVerb;
  const suffix = options.isRunning ? "…" : "";
  return `${verb} ${options.subject}${suffix}`;
}

/**
 * Renders a "Verb <object> [*citation*]" pattern for get-by-id tools.
 * Falls back to just the phrase when no citation is found on the input.
 */
function lookupPhrase(options: {
  runningVerb: string;
  doneVerb: string;
  object: string;
  citation: string | null;
  isRunning: boolean;
}): ReactNode {
  const { runningVerb, doneVerb, object, citation, isRunning } = options;
  const verb = isRunning ? runningVerb : doneVerb;
  if (!citation) {
    return isRunning ? `${verb} ${object}…` : `${verb} ${object}`;
  }
  const displayCitation = truncate(citation, TOOL_PARAMETER_MAX_CHARS);
  const trailing = isRunning && !displayCitation.endsWith("…") ? "…" : "";
  return (
    <>
      {verb} {renderUnderline(displayCitation)}
      {trailing}
    </>
  );
}

const CITATION_INPUT_KEYS = ["citation", "section", "id", "nodeId"];

const toolRenderers: Record<string, ToolRenderer> = {
  search_regulations: ({ tool, isRunning }) =>
    searchPhrase({
      runningVerb: "Searching",
      doneVerb: "Searching",
      object: "federal regulations",
      query: readFirstString(tool.toolInput, QUERY_INPUT_KEYS),
      isRunning,
    }),

  search_ca_regulations: ({ tool, isRunning }) =>
    searchPhrase({
      runningVerb: "Searching",
      doneVerb: "Searching",
      object: "California regulations",
      query: readFirstString(tool.toolInput, QUERY_INPUT_KEYS),
      isRunning,
    }),

  search_ny_regulations: ({ tool, isRunning }) =>
    searchPhrase({
      runningVerb: "Searching",
      doneVerb: "Searching",
      object: "New York regulations",
      query: readFirstString(tool.toolInput, QUERY_INPUT_KEYS),
      isRunning,
    }),

  browse_federal_regulations: ({ isRunning }) =>
    isRunning ? "Browsing federal regulations…" : "Browsing federal regulations",

  browse_ca_regulations: ({ isRunning }) =>
    isRunning ? "Browsing California regulations…" : "Browsing California regulations",

  browse_ny_regulations: ({ isRunning }) =>
    isRunning ? "Browsing New York regulations…" : "Browsing New York regulations",

  get_regulation: ({ tool, isRunning }) =>
    lookupPhrase({
      runningVerb: "Looking up",
      doneVerb: "Looking up",
      object: "federal regulation",
      citation: readFirstString(tool.toolInput, CITATION_INPUT_KEYS),
      isRunning,
    }),

  get_ca_regulation: ({ tool, isRunning }) =>
    lookupPhrase({
      runningVerb: "Looking up",
      doneVerb: "Looking up",
      object: "California regulation",
      citation: readFirstString(tool.toolInput, CITATION_INPUT_KEYS),
      isRunning,
    }),

  get_ny_regulation: ({ tool, isRunning }) =>
    lookupPhrase({
      runningVerb: "Looking up",
      doneVerb: "Looking up",
      object: "New York regulation",
      citation: readFirstString(tool.toolInput, CITATION_INPUT_KEYS),
      isRunning,
    }),

  search_compliance: ({ tool, isRunning }) =>
    searchPhrase({
      runningVerb: "Searching",
      doneVerb: "Searching",
      object: "compliance requirements",
      query: sanitizeComplianceSearchQuery(readFirstString(tool.toolInput, QUERY_INPUT_KEYS)),
      isRunning,
    }),

  get_compliance_requirement: ({ tool, isRunning }) => {
    const identifier = firstFriendlyComplianceIdentifier([
      readString(tool.toolInput, "title"),
      readString(tool.toolOutput, "title"),
      readString(tool.toolInput, "name"),
      readString(tool.toolOutput, "name"),
      readFirstString(tool.toolInput, CITATION_INPUT_KEYS),
    ]);
    return lookupPhrase({
      runningVerb: "Looking up",
      doneVerb: "Looking up",
      object: "compliance requirement",
      citation: identifier,
      isRunning,
    });
  },

  search_api: ({ tool, isRunning }) =>
    searchPhrase({
      runningVerb: "Searching",
      doneVerb: "Searching",
      object: "your Encamp data",
      query: sanitizeApiLookupQuery(readFirstString(tool.toolInput, QUERY_INPUT_KEYS)),
      isRunning,
    }),

  describe_operation: ({ tool, isRunning }) => {
    const operationName = readString(tool.toolInput, "name");
    // describe_operation takes a `name` arg, not a query body — synthesize a
    // tool-input shape so describeGraphQLOperation's lookup logic still works.
    const lookupName = operationName
      ? describeGraphQLOperation({
          query: `query Probe { ${operationName} { __typename } }`,
        })
      : "Encamp data";
    return graphQLLookupPhrase({
      runningVerb: "Checking",
      doneVerb: "Checking",
      subject: `how the ${lookupName} lookup works`,
      isRunning,
    });
  },

  execute_graphql: (ctx) => {
    const { tool, isRunning } = ctx;
    // Always use `graphQLLookupPhrase` for execute_graphql, even before the
    // query has streamed in. `describeGraphQLOperation` returns the neutral
    // subject "Encamp data" when the top-level field can't be determined,
    // so the row reads "Looking up Encamp data…" during input streaming
    // and refines to "Looking up facilities in CA…" once the query is
    // parseable. Keeping the verb stable across the swap eliminates the
    // jarring "Searching your Encamp data" → "Looking up facilities in CA"
    // flicker that came from falling through to genericRenderer (which
    // used the server-set placeholder displayName in mid-streaming state).
    return graphQLLookupPhrase({
      runningVerb: "Looking up",
      doneVerb: "Looking up",
      subject: summarizeGraphQLExecution(tool.toolInput),
      isRunning,
    });
  },

  search_support: ({ tool, isRunning }) =>
    searchPhrase({
      runningVerb: "Searching",
      doneVerb: "Searching",
      object: "support articles",
      query: readFirstString(tool.toolInput, QUERY_INPUT_KEYS),
      isRunning,
    }),

  get_support_article: ({ tool, isRunning }) => {
    const title =
      readString(tool.toolInput, "title") ??
      readString(tool.toolOutput, "title") ??
      readFirstString(tool.toolInput, CITATION_INPUT_KEYS);
    return lookupPhrase({
      runningVerb: "Opening",
      doneVerb: "Opening",
      object: "support article",
      citation: title,
      isRunning,
    });
  },

  search_knowledge: ({ tool, isRunning }) =>
    searchPhrase({
      runningVerb: "Searching",
      doneVerb: "Searching",
      object: "the knowledge base",
      query: readFirstString(tool.toolInput, QUERY_INPUT_KEYS),
      isRunning,
    }),

  get_article: ({ tool }) => {
    const title = readString(tool.toolInput, "title") ?? readString(tool.toolOutput, "title");
    const verb = "Opening";
    if (!title) return `${verb} knowledge base article`;
    return (
      <>
        {verb} {renderUnderline(truncate(title, 60))}
      </>
    );
  },

  search_code: ({ tool, isRunning }) =>
    searchPhrase({
      runningVerb: "Searching",
      doneVerb: "Searching",
      object: "code",
      query: readFirstString(tool.toolInput, QUERY_INPUT_KEYS),
      isRunning,
    }),

  web_fetch: ({ tool }) => {
    const url = readString(tool.toolInput, "url");
    if (!url) return "Fetching web page";
    return <>Fetching {renderUnderline(truncate(url, 60))}</>;
  },

  web_search: ({ tool, isRunning }) => {
    const query = readFirstString(tool.toolInput, QUERY_INPUT_KEYS);
    const displayQuery = query ? truncate(query, TOOL_PARAMETER_MAX_CHARS) : null;

    if (isRunning) {
      return displayQuery ? (
        <>
          Searching the web for {renderUnderline(displayQuery)}
          {!displayQuery.endsWith("…") ? "…" : ""}
        </>
      ) : (
        "Searching the web…"
      );
    }

    const urls = readWebSearchUrls(tool.toolOutput);
    if (urls.length === 0) {
      return displayQuery ? (
        <>Searching the web for {renderUnderline(displayQuery)}</>
      ) : (
        "Searching the web"
      );
    }

    const shown = urls.slice(0, 3);
    const extra = urls.length - shown.length;
    return (
      <>
        Searching{" "}
        {shown.map((url, idx) => (
          <Box
            component="span"
            key={url}
            sx={{ whiteSpace: "nowrap" }}
            data-testid="web-search-source"
          >
            {idx > 0 ? ", " : ""}
            <Link
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={stopPropagation}
              color="inherit"
              underline="always"
            >
              {displayUrl(url)}
            </Link>
          </Box>
        ))}
        {extra > 0 ? ` (+${extra} more)` : ""}
      </>
    );
  },
};

export function renderToolTitle(ctx: ToolRenderContext): ReactNode {
  const renderer = toolRenderers[ctx.tool.toolName] ?? genericRenderer;
  return renderer(ctx);
}
